// GENERATED by server/scripts/gen-lexicon-keys.mts from server/src/lexicon-keys.ts — do not edit; run `npm run lexicon-keys -w server`.
/**
 * How the word lookups are keyed (v1 ticket 13): one source for the index builder (lexicon-build.ts), the server's
 * lookup on arrival (lexicon.ts) and the town's building grammar (web/src/town/building-lexicon.ts), so a word is
 * found under the same key everywhere. DOM-free and import-free: `npm run lexicon-keys -w server` copies it word for
 * word to web/src/lexicon-keys.generated.ts (also part of the root build and typecheck).
 */

/** Latin-script languages (Catalan, English, Spanish): lower case, one apostrophe, l.l → l·l. */
export const caNorm = (s: string): string => s.normalize('NFC').toLowerCase().replace(/[’‘`´ʼ]/g, "'").replace(/l\.l/g, 'l·l').trim();
/** Persian script: no vowel marks, tatweel or joiners; Arabic yeh/kaf → Persian; ۀ → ه. */
export const faNorm = (s: string): string => s.normalize('NFC').replace(/[ً-ٰٟـ‌-‏]/g, '')
  .replace(/[يى]/g, 'ی').replace(/ك/g, 'ک').replace(/ۀ/g, 'ه').trim();
/** The key normaliser for a target language. */
export const normFor = (language: string): ((s: string) => string) => (language === 'fa' ? faNorm : caNorm);
/** Whole-item key: lower case, no sentence punctuation, single spaces. */
export const phraseKey = (s: string): string => caNorm(s).replace(/[¿¡?!.,;:"«»“”()…؟،؛]/g, ' ').replace(/\s+/g, ' ').trim();

/** Persian-script words in reading order; a standalone می / نمی joins the verb after it (with a joiner). */
export function faWords(text: string): string[] {
  const raw = text.normalize('NFC').replace(/[؟?!.,،؛;:«»"“”()…-]/g, ' ').split(/\s+/).filter((w) => /\p{Script=Arabic}/u.test(w));
  const out: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    if ((raw[i] === 'می' || raw[i] === 'نمی') && raw[i + 1]) { out.push(`${raw[i]}‌${raw[i + 1]}`); i++; } else out.push(raw[i]);
  }
  return out;
}
/** Other keys for a Persian word: without a written ezafe (-ye, ۀ, kasra), and the stem before a suffix joiner. */
export function faStems(w: string): string[] {
  const s = new Set<string>();
  const t = w.normalize('NFC');
  if (/(ه‌?ی|[ۀِٔ])$/.test(t)) s.add(faNorm(t.replace(/(‌?ی|[ِٔ])$/, '').replace(/ۀ$/, 'ه')));
  if (/[اوه]‌?ی$/.test(t)) s.add(faNorm(t.replace(/‌?ی$/, '')));
  if (t.includes('‌') && !/^(ن?می)‌/.test(t)) s.add(faNorm(t.split('‌')[0]));
  return [...s].filter(Boolean);
}

/** An English contraction → its base word and its ending (don't → do + n't, can't → can + n't, I'm → i + 'm); else null. */
export function enContraction(w: string): [string, string] | null {
  const m = /^(.+?)(n't|'(?:m|re|s|ve|ll|d|t))$/.exec(w);
  if (!m) return null;
  const odd: Record<string, string> = { ca: 'can', wo: 'will', sha: 'shall' };
  return [odd[m[1]] ?? m[1], m[2]];
}

/**
 * Every key the town may look up for one item's text: its words (Persian: as written, without a written ezafe and
 * before a joiner; Latin script: each run of letters, and a word with its apostrophe kept, as in don't or l'aigua,
 * and in English a contraction's base word, do or can), and the whole item when it has more than one word.
 */
export function itemKeys(text: string, language: string): string[] {
  const out = new Set<string>();
  if (language === 'fa') {
    for (const w of faWords(text)) {
      const k = faNorm(w);
      if (k) out.add(k);
      for (const s of faStems(w)) out.add(s);
    }
    return [...out];
  }
  const t = caNorm(text).replace(/\S*\d\S*/g, ' ');
  for (const w of t.match(/[\p{L}·]+/gu) ?? []) if (/\p{L}/u.test(w)) out.add(w);
  for (const w of t.match(/\p{L}[\p{L}·']*'[\p{L}·']*\p{L}/gu) ?? []) {
    out.add(w);
    const base = language === 'en' ? enContraction(w)?.[0] : undefined;
    if (base) out.add(base);
  }
  const p = phraseKey(text);
  if (/[\s'-]/.test(p)) out.add(p);
  return [...out];
}
